/**
 * Markdown -> HTML donusumu ve sanitizasyon. TEK bogaz noktasi.
 *
 * ## SIRA YUK TASIYOR - degistirmeyin
 *
 *   1. LaTeX'i stash'le  (\[..\] ve \(..\))
 *   2. marked.parse
 *   3. unstash
 *   4. DOMPurify.sanitize
 *   5. KaTeX'i SANITIZE EDILMIS DOM uzerinde kostur
 *
 * Her adimin sirasi bir sebeple orada ve iyi niyetli bir "refactor" davet
 * ediyor; o yuzden gerekce burada yazili.
 *
 * **1 neden 2'den once:** markdown LaTeX icindeki `\`, `_` ve `*`
 * karakterlerini BOZUYOR (`a_b` -> italik, `\\` -> kacis). Stash
 * yer tutucusu ALFANUMERIK secildi (`KATEXSTASH0ENDSTASH`) cunku markdown
 * alfanumerik metne dokunmuyor - bu ozellik korunmali.
 *
 * **4 neden 5'ten once DEGIL, 5 neden 4'ten SONRA:** DOMPurify, KaTeX'in
 * MathML/`annotation` ciktisinin parcalarini SILIYOR; once KaTeX kosarsa
 * render ve erisilebilirlik bozulur. Sonra kostumak GUVENLI cunku KaTeX'in
 * GIRDISI bir LaTeX string'i (HTML degil) ve kendi ciktisini escape ediyor.
 * `throwOnError: false` ve varsayilan `trust: false` korunuyor.
 *
 * ## `breaks: true` KORUNUYOR
 *
 * Kaldirilirsa mevcut TUM yanitlarin satir sonlari sessizce degisir -
 * kullanicinin gecmisi bozulmus gibi gorunur.
 */

import DOMPurify from 'dompurify'
import { marked } from 'marked'

// Alfanumerik yer tutucu: markdown buna DOKUNMUYOR. Bu ozellik korunmali -
// ornegin `%%0%%` gibi bir sey markdown tarafindan islenebilirdi.
const STASH_PREFIX = 'KATEXSTASH'
const STASH_SUFFIX = 'ENDSTASH'

// Blok: \[ ... \]  Satir ici: \( ... \)
// `[\s\S]` cok satirli formulleri de yakaliyor.
const MATH_RE = /\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\)/g

export interface StashResult {
  text: string
  /** Yer tutucu indeksinden LaTeX kaynagina. */
  items: { latex: string; display: boolean }[]
}

/** LaTeX bloklarini yer tutucularla degistirir. */
export function stashMath(text: string): StashResult {
  const items: StashResult['items'] = []
  const replaced = text.replace(MATH_RE, (_match, block, inline) => {
    const isDisplay = block !== undefined
    items.push({ latex: (isDisplay ? block : inline) as string, display: isDisplay })
    return `${STASH_PREFIX}${items.length - 1}${STASH_SUFFIX}`
  })
  return { text: replaced, items }
}

/** Yer tutucularin yerine KaTeX'in isleyecegi isaretlemeyi koyar. */
export function unstashMath(html: string, items: StashResult['items']): string {
  return html.replace(
    new RegExp(`${STASH_PREFIX}(\\d+)${STASH_SUFFIX}`, 'g'),
    (_match, index: string) => {
      const item = items[Number(index)]
      if (!item) return ''
      // Delimiter'lari GERI koyuyoruz: KaTeX'in auto-render'i onlari ariyor.
      // HTML'e giden LaTeX kaynagi ESCAPE ediliyor - aksi halde `<` iceren
      // bir formul isaretlemeyi bozardi.
      const escaped = escapeHtml(item.latex)
      return item.display ? `\\[${escaped}\\]` : `\\(${escaped}\\)`
    },
  )
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Sabit allowlist. `marked` + `hljs` ciktisi icin gereken minimum.
const ALLOWED_TAGS = [
  'p', 'br', 'hr', 'strong', 'em', 'del', 'code', 'pre', 'blockquote',
  'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'span', 'div',
  // KaTeX ciktisi
  'math', 'semantics', 'mrow', 'mi', 'mo', 'mn', 'msup', 'msub', 'mfrac',
  'msqrt', 'annotation', 'mtext', 'mspace', 'mstyle', 'munder', 'mover',
]

const ALLOWED_ATTR = ['href', 'title', 'alt', 'src', 'width', 'height', 'class', 'aria-hidden']

let hookInstalled = false

function installHook(): void {
  if (hookInstalled) return
  hookInstalled = true
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      // Yeni sekmede acilan her baglantiya `noopener`: aksi halde acilan
      // sayfa `window.opener` uzerinden bizim sekmemizi yonlendirebilir.
      node.setAttribute('rel', 'noopener noreferrer')
      node.setAttribute('target', '_blank')
    }
  })
}

/** Markdown'i GUVENLI HTML'e cevirir. Hattaki TEK bogaz noktasi. */
export function renderMarkdown(source: string): string {
  installHook()

  // 1. LaTeX'i markdown'dan KORU.
  const { text, items } = stashMath(source)

  // 2. Markdown. `breaks: true` KORUNUYOR (bkz. modul docstring'i).
  const parsed = marked.parse(text, { breaks: true, gfm: true, async: false }) as string

  // 3. Yer tutucular geri.
  const withMath = unstashMath(parsed, items)

  // 4. Sanitize. KaTeX BUNDAN SONRA kosuyor (cagiran tarafta, DOM uzerinde).
  return DOMPurify.sanitize(withMath, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // `javascript:` gibi semalar engelleniyor.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|data:image\/):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  })
}
